import React from 'react';
import { DetailedItem, FloatingLabelInput } from '../../../../../../ui';
import { useEditableForm } from '../hooks/useEditableForm';
import { SettingsCard } from './SettingsCard';

interface ResponsibleTeamData {
  adminEmail: string;
  adminName: string;
  adminPhone: string;
  backupAdminEmail: string;
  backupAdminName: string;
  department: string;
}

interface IResponsibleTeam {
  onSave?: (data: ResponsibleTeamData) => Promise<void> | void;
}

export const ResponsibleTeam: React.FC<IResponsibleTeam> = ({ onSave }) => {
  const initialData: ResponsibleTeamData = {
    adminEmail: 'admin@acmecorp.com',
    adminName: 'John Smith',
    adminPhone: '+1 (555) 123-4567',
    backupAdminEmail: 'backup@acmecorp.com',
    backupAdminName: 'Jane Doe',
    department: 'IT Operations',
  };

  const { isEditing, formData, isSaving, handleEditToggle, handleSave, handleInputChange } = useEditableForm({
    initialData,
    onSave: async (data) => {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      if (onSave) {
        await onSave(data);
      }
    },
  });

  const icon = (
    <svg className='w-5 h-5 text-blue-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' />
    </svg>
  );

  return (
    <div className='md:col-span-2'>
      <SettingsCard
          title='Responsible Team'
          description='Manage administrative contacts and responsible personnel'
          icon={icon}
          isEditing={isEditing}
          isSaving={isSaving}
          onEditToggle={handleEditToggle}
          onSave={handleSave}
      >
        { isEditing ? (
          // Edit Mode
          <>
            <div className='mb-6'>
              <h3 className='text-md font-medium text-gray-900 mb-4'>Primary Administrator</h3>
              <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
                <FloatingLabelInput
                    label='Admin Name'
                    value={formData.adminName}
                    onChange={(e) => handleInputChange('adminName', e.target.value)}
                />
                <FloatingLabelInput
                    label='Admin Email'
                    value={formData.adminEmail}
                    onChange={(e) => handleInputChange('adminEmail', e.target.value)}
                />
                <FloatingLabelInput
                    label='Admin Phone'
                    value={formData.adminPhone}
                    onChange={(e) => handleInputChange('adminPhone', e.target.value)}
                />
              </div>
            </div>

            <div className='mb-6'>
              <h3 className='text-md font-medium text-gray-900 mb-4'>Backup Administrator</h3>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                <FloatingLabelInput
                    label='Backup Admin Name'
                    value={formData.backupAdminName}
                    onChange={(e) => handleInputChange('backupAdminName', e.target.value)}
                />
                <FloatingLabelInput
                    label='Backup Admin Email'
                    value={formData.backupAdminEmail}
                    onChange={(e) => handleInputChange('backupAdminEmail', e.target.value)}
                />
              </div>
            </div>

            <div>
              <FloatingLabelInput
                  label='Department'
                  value={formData.department}
                  onChange={(e) => handleInputChange('department', e.target.value)}
              />
            </div>
          </>
        ) : (
          // View Mode
          <>
            <div className='mb-6'>
              <h3 className='text-md font-medium text-gray-900 mb-4'>Primary Administrator</h3>
              <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
                <div className='p-4 bg-gray-50 rounded-lg'>
                  <DetailedItem
                      label='Admin Name'
                      text={formData.adminName}
                  />
                </div>
                <div className='p-4 bg-gray-50 rounded-lg'>
                  <DetailedItem
                      label='Admin Email'
                      text={formData.adminEmail}
                  />
                </div>
                <div className='p-4 bg-gray-50 rounded-lg'>
                  <DetailedItem
                      label='Admin Phone'
                      text={formData.adminPhone}
                  />
                </div>
              </div>
            </div>

            <div className='mb-6'>
              <h3 className='text-md font-medium text-gray-900 mb-4'>Backup Administrator</h3>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                <div className='p-4 bg-gray-50 rounded-lg'>
                  <DetailedItem
                      label='Backup Admin Name'
                      text={formData.backupAdminName}
                  />
                </div>
                <div className='p-4 bg-gray-50 rounded-lg'>
                  <DetailedItem
                      label='Backup Admin Email'
                      text={formData.backupAdminEmail}
                  />
                </div>
              </div>
            </div>

            <div className='p-4 bg-blue-50 rounded-lg border border-blue-200'>
              <DetailedItem
                  label='Department'
                  text={formData.department}
                  helpertext="The department responsible for managing this organization's settings."
              />
            </div>
          </>
        ) }
      </SettingsCard>
    </div>
  );
};
